import * as Sentry from "@sentry/browser";
import { useStoreState } from "pullstate";
import { useCallback, useEffect, useRef, useState } from "react";
import { getIsWrapOrUnwrap } from "utils/tokenSelectorUtils";
import { signTypedData } from "wagmi/actions";
import { ConfirmTradeModal } from "../../components/Modal/instance/ConfirmTrade/ConfirmTrade";
import { RegionRestrictedModal } from "../../components/Modal/instance/RegionRestrictedModal/RegionRestrictedModal";
import { MODAL_SELECTORS } from "../../components/Modal/instance/modalSelectors";
import { BLOCK_EXPLORER_URL_BY_NETWORK } from "../../config/consts";
import { CommonButtonState } from "../../enums/CommonButtonState";
import { ReactComponent as RefreshIcon } from "../../img/trade-interface/refresh.svg";
import { ReactComponent as EtherscanIcon } from "../../img/trade-interface/view_transaction.svg";
import { GlobalStore, IGlobalStore } from "../../store/global/GlobalStore";
import { getQuote } from "../../store/global/getQuote";
import { OrderStatus } from "../../types/orderStatus";
import { getOrderType } from "../../utils/orderTypeUtils";
import {
  getParamDomain,
  getParamTypes,
  getSignatureData,
  isQuantityLargerThanBalance,
} from "../../utils/orderUtils";
import { isUserUSBased } from "../../utils/regionRestrictUtils";
import { Hash } from "viem";

export const ReviewActionButtonState = {
  ...CommonButtonState,
  ConfirmOrder: "ConfirmOrder",
  QuoteExpired: "QuoteExpired",
  ViewTransaction: "ViewTransaction",
  NoPricing: "NoPricing",
};

export type ReviewActionButtonStateKeys = keyof typeof ReviewActionButtonState;

const validateOrderEntry = (store: IGlobalStore): ReviewActionButtonStateKeys => {
  if (
    store.orderStatus === OrderStatus.IN_PROGRESS ||
    store.orderStatus === OrderStatus.COMPLETED
  ) {
    return ReviewActionButtonState.ViewTransaction as ReviewActionButtonStateKeys;
  }
  if (!store.address) {
    return ReviewActionButtonState.WalletNotConnected as ReviewActionButtonStateKeys;
  }

  if (isQuantityLargerThanBalance(store)) {
    return ReviewActionButtonState.InsufficientBalance as ReviewActionButtonStateKeys;
  }

  if (!store.quote) {
    return ReviewActionButtonState.NoPricing as ReviewActionButtonStateKeys;
  }

  if (store.quoteExpired) {
    return ReviewActionButtonState.QuoteExpired as ReviewActionButtonStateKeys;
  }

  return ReviewActionButtonState.ConfirmOrder as ReviewActionButtonStateKeys;
};

const BUTTON_TEXT_MAP: Record<ReviewActionButtonStateKeys, string> = {
  WalletNotConnected: "Connect Wallet",
  InsufficientBalance: "Enter Amounts",
  WrongNetwork: "Enter Amounts",
  NoPricing: "Enter Amounts",
  ConfirmOrder: "Confirm Order",
  QuoteExpired: "Refresh Quote",
  ViewTransaction: "View Transaction",
};

const BUTTON_DISABLED_MAP: Record<ReviewActionButtonStateKeys, boolean> = {
  WalletNotConnected: false,
  InsufficientBalance: true,
  WrongNetwork: true,
  NoPricing: true,
  ConfirmOrder: false,
  QuoteExpired: false,
  ViewTransaction: false,
};

const BUTTON_STYLES: Record<string, string> = {
  HappyState: "btn-bebop-primary text-white",
  ErrorState: "btn-grey",
  InbetweenState: "btn bebop-white-btn btn-lg btn-block button-font-size",
};

const BUTTON_STYLE_MAP: Record<ReviewActionButtonStateKeys, string> = {
  WalletNotConnected: BUTTON_STYLES.HappyState,
  InsufficientBalance: BUTTON_STYLES.ErrorState,
  WrongNetwork: BUTTON_STYLES.InbetweenState,
  NoPricing: BUTTON_STYLES.ErrorState,
  ConfirmOrder: BUTTON_STYLES.HappyState,
  QuoteExpired: BUTTON_STYLES.InbetweenState,
  ViewTransaction: BUTTON_STYLES.InbetweenState,
};

export const ReviewActionButton = () => {
  const [errorState, setErrorState] = useState<ReviewActionButtonStateKeys | undefined>(undefined);
  const [buttonText, setButtonText] = useState<string>("");
  // We need to use a ref here because we don't want to wait for the state to update
  const isSigningOrderRef = useRef(false);
  const state = useStoreState(GlobalStore);

  useEffect(() => {
    setErrorState(validateOrderEntry(state));
  }, [state]);

  const getClassName = useCallback((): string => {
    if (errorState) {
      return BUTTON_STYLE_MAP[errorState];
    }
    return BUTTON_STYLES.HappyState;
  }, [errorState]);

  useEffect(() => {
    if (!errorState) {
      // Push to Order Review
      setButtonText("Review Order");
    } else {
      setButtonText(BUTTON_TEXT_MAP[errorState]);
    }
  }, [errorState]);

  const {
    makerTokens,
    takerTokens,
    network,
    orderType,
    quote,
    address,
    chain,
    tokenMetadata,
    receiver,
  } = GlobalStore.useState(s => {
    return {
      makerTokens: s.makerTokens,
      takerTokens: s.takerTokens,
      network: s.network,
      orderType: getOrderType(s),
      quote: s.quote,
      address: s.address,
      chain: s.network,
      tokenMetadata: s.tokens,
      receiver: s.receiver,
    };
  });

  const getButtonIcon = useCallback(() => {
    if (errorState === ReviewActionButtonState.ViewTransaction) {
      return (
        <span style={{ marginRight: "8px" }}>
          <EtherscanIcon />
        </span>
      );
    }
    if (errorState === ReviewActionButtonState.QuoteExpired) {
      return (
        <span style={{ marginRight: "8px" }}>
          <RefreshIcon />
        </span>
      );
    }
  }, [errorState]);

  const refreshQuote = useCallback(() => {
    if (orderType) {
      void getQuote
        .run({
          orderType,
          takerTokens,
          makerTokens,
          network,
          address: address as `0x${string}`,
          tokenMetadata,
          receiver,
          permitType: state.permitType,
        })
        .then(() => {
          GlobalStore.update(s => {
            s.quoteExpired = false;
          });
        });
    }
  }, [
    orderType,
    takerTokens,
    makerTokens,
    network,
    address,
    tokenMetadata,
    receiver,
    state.permitType,
  ]);

  const signOrder = useCallback(() => {
    isSigningOrderRef.current = true;
    GlobalStore.update(s => {
      s.modalSelector = MODAL_SELECTORS.CONFIRM_TRADE;
      s.orderStatus = OrderStatus.CONFIRMING;
    });

    if (quote === undefined) {
      Sentry.captureException(`Sign order function called with quote being undefined`);
      return;
    }

    const message = quote.toSign;

    if (message !== undefined) {
      const domain = getParamDomain(chain);
      const types = getParamTypes();
      if (domain && types) {
        signTypedData({
          // @ts-expect-error ChainId needs to be a number not a BigInt (which is inferred from uint256)
          domain,
          types,
          message,
          primaryType: "Aggregate",
        })
          .then(signedData => {
            const formattedSignedData = getSignatureData(signedData);
            GlobalStore.update(s => {
              s.orderStatus = OrderStatus.IN_PROGRESS;
              s.signedMessage = {
                message: message,
                //@ts-expect-error Wagmi has inconsistent Chain typing as of wagmi@0.11.7
                chain: chain,
                quote: quote,
                signature: formattedSignedData.signature as Hash,
              };
            });
          })
          .catch(e => {
            /**
             * An exception is thrown when the user rejects to sign
             * In that case throw them back to Order Review
             */
            console.error(e);
            Sentry.captureException(`signTypedData function failed`, { extra: { error: e } });
            GlobalStore.update(s => {
              s.signedMessage = undefined;
              s.orderStatus = OrderStatus.REVIEW;
              s.modalSelector = undefined;
            });
          })
          .finally(() => {
            Sentry.captureMessage(`signTypedData function completed - unsetting isSigningOrderRef`);
            isSigningOrderRef.current = false;
          });
      } else {
        console.error(domain, types);
        Sentry.captureException(
          `Attempted to sign an order but domain and param types were invalid`
        );
      }
    } else {
      Sentry.captureException(`Attempted to sign an order without a quote message`);
    }
  }, [chain, quote]);

  const handleOnClick = useCallback(async () => {
    if (errorState === ReviewActionButtonState.ViewTransaction) {
      const txHash = getIsWrapOrUnwrap(
        takerTokens.map(t => t.token),
        makerTokens.map(t => t.token),
        chain
      )
        ? state.wrapQuote?.hash
        : state.order?.txHash;
      if (state.order?.status !== "Success") {
        // TODO: Push to error state
        console.log(state.order);
      }
      const url = txHash ? `${BLOCK_EXPLORER_URL_BY_NETWORK[state.network.id]}tx/${txHash}` : "";
      window.open(url);
    }
    const isUSBased = await isUserUSBased();
    if (isUSBased) {
      GlobalStore.update(s => {
        s.modalSelector = MODAL_SELECTORS.REGION_RESTRICTED;
      });
      return;
    }
    if (errorState === ReviewActionButtonState.QuoteExpired) {
      refreshQuote();
    }
    if (errorState === ReviewActionButtonState.ConfirmOrder) {
      if (isSigningOrderRef.current) {
        Sentry.captureMessage(`Attempted to sign an order while isSigningOrderRef is true`);
        return;
      } else {
        isSigningOrderRef.current = true;
        signOrder();
      }
    } else if (errorState === ReviewActionButtonState.WalletNotConnected) {
      GlobalStore.update(s => {
        s.modalSelector = MODAL_SELECTORS.WALLET_CONNECT;
      });
    }
  }, [
    errorState,
    takerTokens,
    makerTokens,
    chain,
    state.wrapQuote?.hash,
    state.order,
    state.network.id,
    refreshQuote,
    signOrder,
  ]);

  const getDisabledAtr = useCallback(() => {
    if (errorState) {
      return BUTTON_DISABLED_MAP[errorState];
    }
    return BUTTON_DISABLED_MAP.ConfirmOrder;
  }, [errorState]);

  return (
    <>
      <ConfirmTradeModal />
      <RegionRestrictedModal />
      <button
        type="button"
        className={`btn-block button-font-size ` + getClassName()}
        onClick={() => void handleOnClick()}
        disabled={getDisabledAtr()}
        data-testid="review-action-btn"
      >
        {getButtonIcon()}
        {buttonText}
      </button>
    </>
  );
};
